import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppLayout from "../../components/layout/AppLayout";
import { productService } from "../../services/productService";
import { uploadImageToCloudinary } from "../../lib/cloudinary";
import { useNotification } from "../../context/NotificationContext";

const EditProduct = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const productId = searchParams.get("id"); // Mengambil ID barang dari URL (?id=...)

  // --- MEMORI (STATE): Menyimpan data barang yang sedang diubah ---
  const [productData, setProductData] = useState({
    name: "",
    sku: "",
    category: "Barang Luar",
    price: 0,
    stock_level: 0,
    min_stock_level: 10,
    description: "",
    image_url: "",
    image_public_id: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * --- LOGIKA: Mengambil Data Barang Lama ---
   * Saat halaman dibuka, cari data barang berdasarkan ID-nya di database.
   */
  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const data = await productService.getProductById(productId);

      if (data) {
        setProductData(data);
        setPreviewUrl(data.image_url || "");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      showNotification({
        type: "error",
        title: "Gagal Memuat",
        message: "Gagal memuat data produk.",
      });
      navigate("/inventory");
    } finally {
      setLoading(false);
    }
  }, [productId, navigate, showNotification]);

  // Jalankan pengambilan data otomatis saat masuk ke halaman ini
  useEffect(() => {
    if (productId) {
      fetchProduct();
    } else {
      navigate("/inventory");
    }
  }, [productId, fetchProduct, navigate]);

  /**
   * --- LOGIKA: Menangani Perubahan Gambar ---
   */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  /**
   * --- LOGIKA UTAMA: Menyimpan Perubahan ---
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      let finalImageUrl = productData.image_url;
      let finalPublicId = productData.image_public_id;

      // 1. Jika ada foto baru, upload dan hapus foto lama dari Cloudinary
      if (imageFile) {
        const uploadResult = await uploadImageToCloudinary(
          imageFile,
          productData.image_public_id,
        );
        finalImageUrl = uploadResult.url;
        finalPublicId = uploadResult.public_id;
      }

      // Gabungkan data lama dengan perubahan baru
      const payload = {
        ...productData,
        image_url: finalImageUrl,
        image_public_id: finalPublicId,
        price: Number(productData.price),
        stock_level: Number(productData.stock_level),
        min_stock_level: Number(productData.min_stock_level),
      };

      // 2. Simpan update ke Database
      await productService.updateProduct(productId, payload);

      showNotification({
        type: "success",
        title: "Berhasil",
        message: "Data produk berhasil diperbarui.",
      });
      navigate("/inventory");
    } catch (error) {
      console.error("Error updating product:", error);
      showNotification({
        type: "error",
        title: "Gagal Menyimpan",
        message: "Gagal memperbarui produk: " + error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * --- LOGIKA: Menghapus Produk ---
   * Juga menghapus foto dari Cloudinary agar memori tidak penuh.
   */
  const handleDelete = () => {
    showNotification({
      type: "confirm",
      title: "Konfirmasi Hapus",
      message:
        "Apakah Anda yakin ingin menghapus produk ini? Tindakan ini tidak dapat dibatalkan.",
      onConfirm: async () => {
        try {
          setIsSubmitting(true);

          // Hapus foto dari Cloudinary jika ada
          if (productData.image_public_id) {
            try {
              await productService.deleteCloudinaryAsset(productData.image_public_id);
            } catch (err) {
              console.error("Non-blocking Cloudinary error:", err);
            }
          }

          // Hapus data dari Database
          await productService.deleteProduct(productId);

          showNotification({
            type: "success",
            title: "Dihapus",
            message: "Produk berhasil dihapus.",
          });
          navigate("/inventory");
        } catch (error) {
          console.error("Error deleting product:", error);
          showNotification({
            type: "error",
            title: "Gagal Menghapus",
            message: "Gagal menghapus produk: " + error.message,
          });
        } finally {
          setIsSubmitting(false);
        }
      },
    });
  };

  // Tampilkan layar loading jika data belum siap
  if (loading) {
    return (
      <AppLayout pageTitle="Ubah Produk">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-on-surface-variant font-medium">
              Memuat Produk...
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="Ubah Produk">
      <div className="p-4 md:p-8 min-h-[calc(100vh-64px)] space-y-6">
        {/* Navigasi Breadcrumbs */}
        <nav className="flex items-center gap-2 text-[10px] font-label uppercase tracking-widest text-on-surface-variant mb-2">
          <button
            onClick={() => navigate("/inventory")}
            className="hover:text-primary transition-colors font-bold"
          >
            Inventaris
          </button>
          <span className="material-symbols-outlined text-[14px]">
            chevron_right
          </span>
          <span className="text-on-surface font-black">Ubah Produk</span>
        </nav>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* BAGIAN ATAS: Visual Produk */}
          <div className="bg-surface-container-lowest rounded-[2rem] overflow-hidden border border-outline-variant/10 shadow-sm ambient-bloom font-manrope">
            <div className="p-6 md:p-10 flex flex-col items-center text-center">
              <div className="relative group w-full max-w-md aspect-video rounded-3xl overflow-hidden bg-surface-container-highest border-2 border-dashed border-outline-variant/30 flex items-center justify-center transition-all hover:border-primary/50 shadow-inner">
                {previewUrl ? (
                  <img
                    alt={productData.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    src={previewUrl}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-3 text-outline">
                    <span className="material-symbols-outlined text-6xl">
                      image
                    </span>
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em]">
                      Tidak Ada Gambar
                    </p>
                  </div>
                )}

                {/* Tombol Overlay untuk Ganti Gambar */}
                <label className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-all">
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={isSubmitting}
                  />
                  <div className="opacity-0 group-hover:opacity-100 bg-white text-primary px-6 py-3 rounded-full shadow-2xl transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 flex items-center gap-2 font-bold text-xs uppercase tracking-wider">
                    <span className="material-symbols-outlined text-sm">
                      cached
                    </span>
                    Ganti Gambar
                  </div>
                </label>
              </div>

              <div className="mt-8 space-y-2">
                <h3 className="font-headline text-3xl md:text-4xl font-black text-on-surface tracking-tighter">
                  {productData.name}
                </h3>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <span className="px-3 py-1 bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest rounded-full border border-primary/20">
                    {productData.category}
                  </span>
                  <span className="px-3 py-1 bg-surface-container-highest text-on-surface-variant text-[10px] font-mono font-bold rounded-full border border-outline-variant/20 uppercase tracking-wider">
                    #{productData.sku}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* BAGIAN BAWAH: Formulir Edit Detail */}
          <div className="bg-surface-container-lowest rounded-[2rem] p-6 md:p-12 border border-outline-variant/10 shadow-xl shadow-black/[0.02]">
            <div className="mb-12 text-left md:text-center">
              <h4 className="font-headline text-xl md:text-2xl font-black text-on-surface tracking-tight uppercase tracking-[0.1em]">
                Modifikasi Detail Produk
              </h4>
              <p className="text-xs md:text-sm text-outline mt-1 font-medium">
                Sesuaikan informasi stok, harga, dan atribusi produk lainnya.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Nama Produk */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Nama Display Produk
                  </label>
                  <input
                    required
                    className="w-full bg-surface-container-highest border-none rounded-[1.5rem] px-6 py-4.5 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none font-bold shadow-inner"
                    type="text"
                    value={productData.name}
                    onChange={(e) =>
                      setProductData({ ...productData, name: e.target.value })
                    }
                  />
                </div>

                {/* Kategori */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Klasifikasi Stok
                  </label>
                  <select
                    className="w-full bg-surface-container-highest border-none rounded-[1.5rem] px-6 py-4.5 text-sm appearance-none focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none font-black"
                    value={productData.category}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        category: e.target.value,
                      })
                    }
                  >
                    <option>Barang Luar</option>
                    <option>Barang Dalam</option>
                  </select>
                </div>

                {/* SKU (Terkunci) */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-on-surface-variant ml-1 opacity-50">
                    Master SKU code
                  </label>
                  <input
                    required
                    className="w-full bg-surface-container border-none rounded-[1.5rem] px-6 py-4.5 text-sm font-mono focus:ring-0 outline-none opacity-40 cursor-not-allowed italic font-bold"
                    type="text"
                    value={productData.sku}
                    disabled
                  />
                </div>

                {/* Harga */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Harga Jual Per Unit
                  </label>
                  <div className="relative">
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-primary font-black text-sm">
                      Rp
                    </span>
                    <input
                      required
                      className="w-full bg-surface-container-highest border-none rounded-[1.5rem] pl-14 pr-6 py-4.5 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none font-black shadow-inner"
                      type="number"
                      value={productData.price}
                      onChange={(e) =>
                        setProductData({
                          ...productData,
                          price: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                {/* Stok Control */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Manajemen Stok Fisik
                  </label>
                  <div className="flex items-center gap-4 bg-surface-container-highest w-full rounded-[1.5rem] p-1.5 shadow-inner">
                    <button
                      className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/50 hover:bg-white transition-all text-primary hover:shadow-lg"
                      type="button"
                      onClick={() =>
                        setProductData({
                          ...productData,
                          stock_level: Math.max(
                            0,
                            productData.stock_level - 1,
                          ),
                        })
                      }
                    >
                      <span className="material-symbols-outlined">remove</span>
                    </button>
                    <input
                      className="bg-transparent border-none text-center flex-1 text-sm font-black focus:ring-0"
                      type="number"
                      value={productData.stock_level}
                      onChange={(e) =>
                        setProductData({
                          ...productData,
                          stock_level: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                    <button
                      className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/50 hover:bg-white transition-all text-primary hover:shadow-lg"
                      type="button"
                      onClick={() =>
                        setProductData({
                          ...productData,
                          stock_level: Number(productData.stock_level) + 1,
                        })
                      }
                    >
                      <span className="material-symbols-outlined">add</span>
                    </button>
                  </div>
                </div>

                {/* Ambang Batas */}
                <div className="space-y-3">
                  <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                    Level Re-stock (Alert)
                  </label>
                  <input
                    className="w-full bg-surface-container-highest border-none rounded-[1.5rem] px-6 py-4.5 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none font-bold shadow-inner"
                    type="number"
                    value={productData.min_stock_level}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        min_stock_level: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>

              {/* Deskripsi Area */}
              <div className="space-y-3">
                <label className="text-[10px] font-label font-black uppercase tracking-[0.2em] text-primary ml-1">
                  Atribut & Deskripsi Produk
                </label>
                <textarea
                  className="w-full bg-surface-container-highest border-none rounded-[2rem] px-8 py-6 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all outline-none resize-none font-medium shadow-inner"
                  rows={4}
                  value={productData.description || ""}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      description: e.target.value,
                    })
                  }
                />
              </div>

              {/* Tombol Simpan/Hapus */}
              <div className="pt-10 flex flex-col md:flex-row items-center gap-6 border-t border-outline-variant/10">
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                  <button
                    className="w-full sm:w-auto bg-primary text-on-primary font-black px-12 py-5 rounded-[1.75rem] shadow-2xl shadow-primary/30 hover:scale-[1.03] active:scale-[0.98] transition-all disabled:opacity-50 text-xs uppercase tracking-widest"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Syncing..." : "Simpan Perubahan"}
                  </button>
                  <button
                    className="w-full sm:w-auto px-10 py-5 rounded-[1.75rem] text-on-surface-variant font-bold hover:bg-surface-container transition-all text-xs uppercase tracking-widest border border-outline-variant/20"
                    type="button"
                    onClick={() => navigate("/inventory")}
                    disabled={isSubmitting}
                  >
                    Batal
                  </button>
                </div>

                <button
                  className="md:ml-auto w-full md:w-auto font-black flex items-center justify-center gap-3 px-8 py-5 rounded-[1.75rem] bg-error/5 text-error hover:bg-error hover:text-white transition-all text-xs uppercase tracking-widest shadow-lg shadow-error/5 hover:shadow-error/20"
                  type="button"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  <span className="material-symbols-outlined text-lg">
                    delete_forever
                  </span>
                  Hapus Master Data
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default EditProduct;
